import { useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { validarInputExpresionRegular } from '../../utils/generalFunctions';
import { alertaCargandoProceso, alertaGeneral } from '../../utils/alertas';
import apiClient from '../../utils/axiosPetition';
import InputFile from '../../utils/InputFile';
import ImageCropper from '../ImageCropper/ImageCropper';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
const isValidPhoneNumber = (phone) => /^\d{10}$/.test(String(phone).trim());
const isValidIdNumber = (id) => /^\d{6,12}$/.test(String(id).trim());

const CompleteProfileExpert = ({ doctor, setDoctor, setShow }) => {
    const [formData, setFormData] = useState({
        fullName: doctor.fullName || '',
        idType: doctor.idType || 'CC',
        idNumber: doctor.idNumber || '',
        email: doctor.email || '',
        cellNumber: doctor.cellNumber || '',
        description: doctor.description && !doctor.description.includes('Supervisa equipos')
            ? doctor.description
            : '',
        foto_perfil: '',
        ...(!doctor.urlIdDocument && { cedulaPDF: '' }),
        diploma_acta_grado: '',
    });
    const [showCropper, setShowCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);

    const optionalFields = [
        ...(doctor.urlProfessionalPhoto ? ['foto_perfil'] : []),
        ...(doctor.urlIdDocument ? ['cedulaPDF'] : []),
    ];

    const sanitizarValor = (e) => {
        switch (e.target.name) {
            case 'cellNumber':
            case 'idNumber':
                return validarInputExpresionRegular(e, /[^0-9]/g);
            case 'fullName':
                return validarInputExpresionRegular(e, /[^A-Za-záéíóúÁÉÍÓÚñÑ\s]/g);
            default:
                return e.target.value;
        }
    };

    const handleChange = (e) => {
        const { name, type, files } = e.target;

        if (name === 'foto_perfil' && type === 'file' && files?.[0]) {
            setImageToCrop(files[0]);
            setShowCropper(true);
            return;
        }

        if (type === 'file') {
            setFormData((prev) => ({ ...prev, [name]: files?.[0] || '' }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: sanitizarValor(e) }));
    };

    const handleCropComplete = (croppedFile) => {
        setFormData((prev) => ({ ...prev, foto_perfil: croppedFile }));
        setShowCropper(false);
        setImageToCrop(null);
    };

    const handleCropCancel = () => {
        setShowCropper(false);
        setImageToCrop(null);
    };

    const validate = () => {
        const errors = [];
        const labels = {
            fullName: 'Nombre completo',
            idType: 'Tipo de documento',
            idNumber: 'Número de documento',
            email: 'Correo electrónico',
            cellNumber: 'Celular',
            description: 'Descripción de perfil',
            foto_perfil: 'Foto de perfil',
            cedulaPDF: 'Documento de identidad',
            diploma_acta_grado: 'Diploma de pregrado',
        };

        Object.keys(formData).forEach((key) => {
            if (!optionalFields.includes(key) && !formData[key]) {
                errors.push(`• ${labels[key] || key} es obligatorio`);
            }
        });

        if (formData.email && !isValidEmail(formData.email)) {
            errors.push('• El correo electrónico no es válido');
        }
        if (formData.cellNumber && !isValidPhoneNumber(formData.cellNumber)) {
            errors.push('• El celular debe tener 10 dígitos');
        }
        if (formData.idNumber && !isValidIdNumber(formData.idNumber)) {
            errors.push('• El número de documento debe tener entre 6 y 12 dígitos');
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validate();
        if (errors.length) {
            alertaGeneral({
                titulo: 'Completa los datos',
                messageHtml: errors.join('<br/>'),
                isError: true,
            });
            return;
        }

        const clinicInfo = localStorage.getItem('clinicId') || doctor?.clinicInfo || '66bd188dc4f464511048ceac';
        const formDataToSend = new FormData();

        Object.entries(formData).forEach(([key, value]) => {
            if (key === 'foto_perfil' && !value && doctor.urlProfessionalPhoto) return;
            if (value instanceof File) {
                formDataToSend.append(key, value);
            } else if (value !== undefined && value !== null) {
                formDataToSend.append(key, value);
            }
        });

        formDataToSend.append('_id', doctor._id);
        formDataToSend.append('clinicInfo', clinicInfo);
        formDataToSend.append('tipoPerfil', 'experto');

        alertaCargandoProceso({
            titulo: 'Enviando perfil',
            messageHtml: 'Estamos guardando tu información...',
            funcionAsync: async () => {
                try {
                    const res = await apiClient.post('/api/afiliado/prestar-servicios', formDataToSend, {
                        timeout: 120000,
                    });
                    Swal.close();
                    setDoctor(res.data.afiliado);
                    if (res.data.afiliado?.urlProfessionalPhoto) {
                        localStorage.setItem('imageProfile', res.data.afiliado.urlProfessionalPhoto);
                    }
                    await alertaGeneral({
                        titulo: 'Perfil enviado',
                        messageHtml: 'Tu perfil fue enviado. El equipo administrativo lo revisará pronto.',
                    });
                    setShow(false);
                    window.location.reload();
                } catch (error) {
                    Swal.close();
                    alertaGeneral({
                        titulo: 'Error al enviar',
                        messageHtml: error.response?.data?.message || 'No se pudo enviar el perfil. Intenta de nuevo.',
                        isError: true,
                    });
                }
            },
        });
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <button
                type="button"
                onClick={() => setShow(false)}
                className="flex items-center gap-2 text-[#1f94c2] hover:text-[#205087] mb-6 font-medium"
            >
                <FontAwesomeIcon icon={faArrowLeft} />
                Volver
            </button>

            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                    <span className="inline-block px-4 py-1 bg-blue-50 text-[#1f94c2ff] text-sm font-semibold rounded-full mb-3">
                        Completar perfil
                    </span>
                    <h2 className="text-2xl font-bold text-[#70706eff] mb-2">Perfil sin prestación de servicios de salud</h2>
                    <p className="text-sm text-[#6e787aff]">
                        Completa tu información básica para participar en la plataforma sin atender pacientes clínicamente.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-4 sm:p-5 mb-6 shadow-sm">
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <div className="flex-1 text-sm sm:text-base text-gray-700">
                            <h3 className="text-base sm:text-lg font-bold text-orange-800 mb-2">
                                Documentos requeridos para este registro
                            </h3>
                            <p className="mb-3">
                                Tenga listos los siguientes archivos en PDF o imagen (JPG, PNG) antes de continuar:
                            </p>
                            <ul className="list-disc pl-5 space-y-1 mb-3">
                                <li><strong>Foto de perfil</strong> (imagen clara y reciente)</li>
                                <li><strong>Documento de identidad</strong> (cédula o documento equivalente)</li>
                                <li><strong>Diploma o acta de grado de pregrado</strong></li>
                            </ul>
                            <div className="bg-white/70 rounded-lg p-3 border-l-4 border-orange-400">
                                <p className="font-semibold text-orange-700 mb-1">
                                    El registro es una única vez
                                </p>
                                <p className="text-sm text-gray-700">
                                    Este proceso se realiza solo al vincularse por primera vez. Verifica que los documentos sean legibles y correctos para evitar demoras en la aprobación de tu perfil.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                            <input
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento *</label>
                            <select
                                name="idType"
                                value={formData.idType}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                                <option value="CC">Cédula de ciudadanía</option>
                                <option value="CE">Cédula de extranjería</option>
                                <option value="PA">Pasaporte</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Número de documento *</label>
                            <input
                                name="idNumber"
                                value={formData.idNumber}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico *</label>
                            <input
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Celular *</label>
                            <input
                                name="cellNumber"
                                value={formData.cellNumber}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción de perfil *</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                placeholder="Cuéntanos sobre tu experiencia y área de expertise..."
                            />
                        </div>
                    </div>

                    <div className="space-y-4 border-t pt-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Foto de perfil{optionalFields.includes('foto_perfil') ? ' (opcional)' : ' *'}
                            </label>
                            <InputFile
                                name="foto_perfil"
                                onChange={handleChange}
                                fileName={formData.foto_perfil?.name}
                                accept=".jpg,.jpeg,.png,.webp"
                            />
                        </div>
                        {!doctor.urlIdDocument && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Documento de identidad (PDF o imagen) *
                                </label>
                                <InputFile
                                    name="cedulaPDF"
                                    onChange={handleChange}
                                    fileName={formData.cedulaPDF?.name}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Diploma o acta de grado de pregrado *
                            </label>
                            <InputFile
                                name="diploma_acta_grado"
                                onChange={handleChange}
                                fileName={formData.diploma_acta_grado?.name}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                        </div>
                    </div>

                    <button type="submit" className="button-add w-full py-3 text-base font-medium">
                        Enviar perfil para revisión
                    </button>
                </form>
            </div>

            {showCropper && imageToCrop && (
                <ImageCropper
                    imageFile={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
        </div>
    );
};

export default CompleteProfileExpert;
